import { create } from 'zustand'
import { supabase } from '@/lib/supabase'
import type { Task, TaskFormData, TaskCompletion } from '@/types'

interface TaskState {
  tasks: Task[]
  completions: TaskCompletion[]
  loading: boolean
  error: string | null
  fetchTasks: () => Promise<void>
  fetchCompletions: () => Promise<void>
  addTask: (data: TaskFormData) => Promise<Task | null>
  updateTask: (id: string, data: Partial<TaskFormData>) => Promise<void>
  deleteTask: (id: string) => Promise<void>
  completeTask: (taskId: string, completedBy: string, timeTaken?: number, notes?: string) => Promise<void>
  skipTask: (taskId: string, date: string) => Promise<void>
}

export const useTaskStore = create<TaskState>((set, get) => ({
  tasks: [],
  completions: [],
  loading: false,
  error: null,

  fetchTasks: async () => {
    set({ loading: true, error: null })
    try {
      const { data, error } = await supabase
        .from('tasks')
        .select('*')
        .order('start_date', { ascending: true })

      if (error) throw error
      set({ tasks: data || [], loading: false })
    } catch (error) {
      set({ error: (error as Error).message, loading: false })
    }
  },

  fetchCompletions: async () => {
    set({ loading: true, error: null })
    try {
      const { data, error } = await supabase
        .from('task_completions')
        .select('*')
        .order('completed_at', { ascending: false })

      if (error) throw error
      set({ completions: data || [], loading: false })
    } catch (error) {
      set({ error: (error as Error).message, loading: false })
    }
  },

  addTask: async (data: TaskFormData) => {
    set({ loading: true, error: null })
    try {
      const { supply_ids, ...taskData } = data

      const { data: newTask, error } = await supabase
        .from('tasks')
        // @ts-ignore - Supabase type inference issue with Insert types
        .insert([taskData])
        .select()
        .single()

      if (error) throw error

      // Link supplies if any
      if (supply_ids.length > 0) {
        const taskSupplies = supply_ids.map((supply_id) => ({
          // @ts-ignore - Supabase type inference issue
          task_id: newTask.id,
          supply_id,
        }))

        const { error: supplyError } = await supabase
          .from('task_supplies')
          // @ts-ignore - Supabase type inference issue with Insert types
          .insert(taskSupplies)

        if (supplyError) throw supplyError
      }

      set((state) => ({
        tasks: [...state.tasks, newTask],
        loading: false,
      }))

      return newTask
    } catch (error) {
      set({ error: (error as Error).message, loading: false })
      return null
    }
  },

  updateTask: async (id: string, data: Partial<TaskFormData>) => {
    set({ loading: true, error: null })
    try {
      const { supply_ids, ...taskData } = data

      const { error } = await supabase
        .from('tasks')
        // @ts-ignore - Supabase type inference issue with Update types
        .update(taskData)
        .eq('id', id)

      if (error) throw error

      // Update supplies if provided
      if (supply_ids) {
        // Delete existing supplies
        await supabase.from('task_supplies').delete().eq('task_id', id)

        // Insert new supplies
        if (supply_ids.length > 0) {
          const taskSupplies = supply_ids.map((supply_id) => ({
            task_id: id,
            supply_id,
          }))

          const { error: supplyError } = await supabase
            .from('task_supplies')
            // @ts-ignore - Supabase type inference issue with Insert types
            .insert(taskSupplies)

          if (supplyError) throw supplyError
        }
      }

      set((state) => ({
        tasks: state.tasks.map((t) =>
          t.id === id ? { ...t, ...taskData, updated_at: new Date().toISOString() } : t
        ),
        loading: false,
      }))
    } catch (error) {
      set({ error: (error as Error).message, loading: false })
    }
  },

  deleteTask: async (id: string) => {
    set({ loading: true, error: null })
    try {
      const { error } = await supabase
        .from('tasks')
        .delete()
        .eq('id', id)

      if (error) throw error

      set((state) => ({
        tasks: state.tasks.filter((t) => t.id !== id),
        loading: false,
      }))
    } catch (error) {
      set({ error: (error as Error).message, loading: false })
    }
  },

  completeTask: async (taskId: string, completedBy: string, timeTaken?: number, notes?: string) => {
    set({ loading: true, error: null })
    try {
      const task = get().tasks.find((t) => t.id === taskId)
      if (!task) throw new Error('Task not found')

      const completedAt = new Date().toISOString()
      const onTime = new Date(completedAt) <= new Date(task.start_date)

      const { data, error } = await supabase
        .from('task_completions')
        // @ts-ignore - Supabase type inference issue with Insert types
        .insert([{
          task_id: taskId,
          completed_by: completedBy,
          completed_at: completedAt,
          time_taken: timeTaken || null,
          on_time: onTime,
          notes: notes || null,
        }])
        .select()
        .single()

      if (error) throw error

      set((state) => ({
        completions: [data, ...state.completions],
        loading: false,
      }))
    } catch (error) {
      set({ error: (error as Error).message, loading: false })
    }
  },

  skipTask: async (taskId: string, date: string) => {
    set({ loading: true, error: null })
    try {
      const task = get().tasks.find((t) => t.id === taskId)
      if (!task) throw new Error('Task not found')

      const updatedSkipDates = [...task.skip_dates, date]

      const { error } = await supabase
        .from('tasks')
        // @ts-ignore - Supabase type inference issue with Update types
        .update({ skip_dates: updatedSkipDates })
        .eq('id', taskId)

      if (error) throw error

      set((state) => ({
        tasks: state.tasks.map((t) =>
          t.id === taskId ? { ...t, skip_dates: updatedSkipDates } : t
        ),
        loading: false,
      }))
    } catch (error) {
      set({ error: (error as Error).message, loading: false })
    }
  },
}))
